// ============================================================
// LiveData — replaces app-live-data Angular component
// Shows attendance marking + attendance status tables with progress bars
// ============================================================
import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store'; // adjust path to your Redux store
import { useLiveData } from './useLiveData';
import ProgressBar from '../shared/ProgressBar';
import AccordionTab from '../shared/AccordionTab';
import { useNavigate } from 'react-router-dom';
import '../dashboard.scss';
const LiveData: React.FC = () => {
  const s = useSelector((state: RootState) => state.session);
  const usertypeid = s.usertypeid ?? '';
  const emisUsertype1 = s.emisUsertype1 ?? '';
  const districtId = s.districtId ?? '';
  const edu_district_id = s.edu_district_id ?? '';
  const blockId = s.blockId ?? '';

  const { state, toggleMarking, toggleAttendance } = useLiveData(
    usertypeid,
    emisUsertype1,
    districtId,
    edu_district_id,
    blockId
  );

  const navigate = useNavigate();
  const att = state.attendance;

  if (state.loading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }
  if (state.error) {
    return <div className="alert alert-danger">{state.error}</div>;
  }

  const isRteUser = usertypeid === '38' || usertypeid === '13';
  const lastUpdated = att?.lastUpdatedData;

  const formatDate = (d: string) =>
    d ? new Date(d).toLocaleDateString('en-GB') : '';

  return (
    <div className="row">
      <div className="col-lg-12 col-xl-12 grid-margin stretch-card">
        <div className="card">
          <div className="card-body text-center">
            {/* Heading */}
            <div className="d-grid gap-2 d-md-block mt-2 mb-2 text-center">
              <button
                className="btn button-color col-sm-3 col-lg-6 col-xl-5 mx-2 headingButton"
                type="button"
              >
                Attendance
              </button>
            </div>

            {/* Toggle buttons */}
            <div className="d-grid gap-2 d-md-block mt-3 mb-2">
              <button
                className={`btn col-sm-3 col-lg-6 col-xl-5 mx-2 ${state.stuToggle ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={toggleMarking}
              >
                Marking Status
              </button>
              <button
                className={`btn col-sm-3 col-lg-6 col-xl-5 mx-2 ${state.stuToggles ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={toggleAttendance}
              >
                Attendance Status
              </button>
            </div>

            <p className="text-center small text-muted">
              (Click on each progress bar to view details)
            </p>
            <p>This data will be updated hourly</p>

            <div className="table-responsive mx-3">
              {/* ======================== MARKING TABLE ======================== */}
              {state.markingtable && (
                <table
                  className="table table-hover mb-0 mt-2"
                  style={{ textAlign: 'left', cursor: 'pointer' }}
                >
                  <tbody>
                    {/* Schools (Students) — hidden for RTE users */}
                    {!isRteUser && (
                      <tr>
                        <td
                          className="align-middle"
                          onClick={() =>
                            navigate('/student-attendance/marking')
                          }
                          title="Click to view data."
                        >
                          Schools (Students)
                        </td>
                        {/* <td
                          className="align-middle"
                          onClick={() =>
                            navigate('/student-attendance/marking')
                          }
                        >
                          {formatDate(
                            lastUpdated?.student_attendance_school_marked
                              ?.Attendance_date
                          )}
                        </td> */}
                        <td>
                          <AccordionTab
                            header={
                              <ProgressBar
                                segments={[
                                  {
                                    percent: att?.stumarkfulper ?? 0,
                                    label: `Fully Marked: ${att?.stumarkful?.toLocaleString('en-IN')}`,
                                    colorClass: 'success',
                                  },
                                  {
                                    percent: att?.stumarpartper ?? 0,
                                    label: `Partially Marked: ${att?.stumarpart?.toLocaleString('en-IN')}`,
                                    colorClass: 'warning',
                                  },
                                  {
                                    percent: att?.stumarunmarper ?? 0,
                                    label: `Not Marked: ${att?.stumarunmar?.toLocaleString('en-IN')}`,
                                    colorClass: 'danger',
                                  },
                                ]}
                              />
                            }
                          >
                            {/* Government breakdown */}
                            <div className="p-2">
                              <h5>Government</h5>
                              <ProgressBar
                                segments={[
                                  {
                                    percent: att?.stumarkfulper ?? 0,
                                    label: `Fully Marked Gov`,
                                    colorClass: 'success',
                                  },
                                  {
                                    percent: att?.stumarpartper ?? 0,
                                    label: `Partially Marked Gov`,
                                    colorClass: 'warning',
                                  },
                                  {
                                    percent: att?.stumarunmarper ?? 0,
                                    label: `Not Marked Gov`,
                                    colorClass: 'danger',
                                  },
                                ]}
                              />
                            </div>
                          </AccordionTab>
                        </td>
                      </tr>
                    )}

                    {/* School (Staff) — hidden for RTE users */}
                    {!isRteUser && (
                      <tr>
                        <td
                          className="align-middle"
                          onClick={() =>
                            navigate('/student-attendance/teacher-marked')
                          }
                          title="Click to view data."
                        >
                          School (Staff)
                        </td>
                        {/* <td
                          className="align-middle"
                          onClick={() =>
                            navigate('/student-attendance/teacher-marked')
                          }
                        >
                          {formatDate(
                            lastUpdated?.teaching_staff_attendance_marked
                              ?.Attendance_date
                          )}
                        </td> */}
                        <td>
                          <AccordionTab
                            header={
                              <ProgressBar
                                segments={[
                                  {
                                    percent: att?.staffmarkper ?? 0,
                                    label: `Marked: ${att?.staffmark?.toLocaleString('en-IN')}`,
                                    colorClass: 'success',
                                  },
                                  {
                                    percent: att?.staffunmarper ?? 0,
                                    label: `Not Marked: ${att?.staffunmar?.toLocaleString('en-IN')}`,
                                    colorClass: 'danger',
                                  },
                                ]}
                              />
                            }
                          >
                            <div className="p-2">
                              <h5>Government</h5>
                              <ProgressBar
                                segments={[
                                  {
                                    percent: att?.staffmarkper ?? 0,
                                    label: `Marked`,
                                    colorClass: 'success',
                                  },
                                  {
                                    percent: att?.staffunmarper ?? 0,
                                    label: `Not Marked`,
                                    colorClass: 'danger',
                                  },
                                ]}
                              />
                            </div>
                          </AccordionTab>
                        </td>
                      </tr>
                    )}

                    {/* School (RTE Students) — only for RTE users */}
                    {isRteUser && (
                      <tr
                        onClick={() =>
                          navigate('/student-attendance/rte-students')
                        }
                      >
                        <td title="Click to view data.">
                          School (RTE Students)
                        </td>
                        {/* <td>
                          {formatDate(
                            lastUpdated?.rte_student_attendance_school_marked
                              ?.Attendance_date
                          )}
                        </td> */}
                        <td>
                          <ProgressBar
                            segments={[
                              {
                                percent: att?.rteschoolmarkper ?? 0,
                                label: `Marked: ${att?.rteschoolmark?.toLocaleString('en-IN')}`,
                                colorClass: 'success',
                              },
                              {
                                percent: att?.rteschoolunmarper ?? 0,
                                label: `Not Marked: ${att?.rteschoolunmar?.toLocaleString('en-IN')}`,
                                colorClass: 'danger',
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    )}

                    {/* ITK Centre — only when graph=true and not RTE user */}
                    {state.graph && !isRteUser && (
                      <tr>
                        <td
                          className="align-middle"
                          onClick={() =>
                            navigate('/student-attendance', {
                              state: { id: 1 },
                            })
                          }
                          title="Click to view data."
                        >
                          ITK Centre
                        </td>
                        {/* <td
                          className="align-middle"
                          onClick={() =>
                            navigate('/student-attendance', {
                              state: { id: 1 },
                            })
                          }
                        >
                          {formatDate(
                            lastUpdated?.itk_attendance_volunteer_marked
                              ?.Attendance_date
                          )}
                        </td> */}
                        <td>
                          <AccordionTab
                            header={
                              <ProgressBar
                                segments={[
                                  {
                                    percent: att?.itkattendancemarkper ?? 0,
                                    label: `Marked: ${att?.itkattendancemark?.toLocaleString('en-IN')}`,
                                    colorClass: 'success',
                                  },
                                  {
                                    percent: att?.itkattendanceunmarper ?? 0,
                                    label: `Not Marked: ${att?.itkattendanceunmark?.toLocaleString('en-IN')}`,
                                    colorClass: 'danger',
                                  },
                                ]}
                              />
                            }
                          >
                            <div className="p-2 text-muted small">
                              ITK Centre detailed breakdown
                            </div>
                          </AccordionTab>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}

              {/* ======================== ATTENDANCE TABLE ======================== */}
              {state.attendancetable && (
                <table
                  className="table table-hover mb-0 mt-2"
                  style={{ textAlign: 'left', cursor: 'pointer' }}
                >
                  <tbody>
                    {/* Students */}
                    <tr>
                      <td
                        className="align-middle"
                        onClick={() =>
                          navigate('/student-attendance/attendance')
                        }
                        title="Click to view data."
                      >
                        Students
                      </td>
                      {/* <td
                        className="align-middle"
                        onClick={() =>
                          navigate('/student-attendance/attendance')
                        }
                      >
                        {formatDate(
                          lastUpdated?.stu_attendance_status?.Attendance_date
                        )}
                      </td> */}
                      <td>
                        <AccordionTab
                          header={
                            <ProgressBar
                              segments={[
                                {
                                  percent: att?.stuattendpresentper ?? 0,
                                  label: `Present: ${att?.stuattendpresent?.toLocaleString('en-IN')}`,
                                  colorClass: 'success',
                                },
                                {
                                  percent: att?.stuattendabsentper ?? 0,
                                  label: `Absent: ${att?.stuattendabsent?.toLocaleString('en-IN')}`,
                                  colorClass: 'danger',
                                },
                              ]}
                            />
                          }
                        >
                          <div className="p-2">
                            <h5>Government</h5>
                            <ProgressBar
                              segments={[
                                {
                                  percent: att?.stuattendpresentper ?? 0,
                                  label: `Present`,
                                  colorClass: 'success',
                                },
                                {
                                  percent: att?.stuattendabsentper ?? 0,
                                  label: `Absent`,
                                  colorClass: 'danger',
                                },
                              ]}
                            />
                          </div>
                        </AccordionTab>
                      </td>
                    </tr>

                    {/* Teaching Staff */}
                    <tr>
                      <td
                        className="align-middle"
                        onClick={() =>
                          navigate('/student-attendance/teacher-attendance')
                        }
                        title="Click to view data."
                      >
                        Teaching Staff
                      </td>
                      {/* <td
                        className="align-middle"
                        onClick={() =>
                          navigate('/student-attendance/teacher-attendance')
                        }
                      >
                        {formatDate(
                          lastUpdated?.teachingstaff_attendance_status
                            ?.Attendance_date
                        )}
                      </td> */}
                      <td>
                        <AccordionTab
                          header={
                            <ProgressBar
                              segments={[
                                {
                                  percent: att?.teachstaffpresentper ?? 0,
                                  label: `Present: ${att?.teachstaffpresent?.toLocaleString('en-IN')}`,
                                  colorClass: 'success',
                                },
                                {
                                  percent: att?.teachstaffabsentper ?? 0,
                                  label: `Absent: ${att?.teachstaffabsent?.toLocaleString('en-IN')}`,
                                  colorClass: 'danger',
                                },
                              ]}
                            />
                          }
                        >
                          <div className="p-2">
                            <h5>Government</h5>
                            <ProgressBar
                              segments={[
                                {
                                  percent: att?.teachstaffpresentper ?? 0,
                                  label: `Present`,
                                  colorClass: 'success',
                                },
                                {
                                  percent: att?.teachstaffabsentper ?? 0,
                                  label: `Absent`,
                                  colorClass: 'danger',
                                },
                              ]}
                            />
                          </div>
                        </AccordionTab>
                      </td>
                    </tr>

                    {/* ITK Students attendance */}
                    {state.graph && (
                      <tr>
                        <td
                          className="align-middle"
                          onClick={() =>
                            navigate('/student-attendance', {
                              state: { id: 2 },
                            })
                          }
                          title="Click to view data."
                        >
                          ITK Students
                        </td>
                        {/* <td className="align-middle" /> */}
                        <td>
                          <ProgressBar
                            segments={[
                              {
                                percent: att?.itkstupresentper ?? 0,
                                label: `Present: ${att?.itkstupresent?.toLocaleString('en-IN')}`,
                                colorClass: 'success',
                              },
                              {
                                percent: att?.itkstuabsentper ?? 0,
                                label: `Absent: ${att?.itkstuabsent?.toLocaleString('en-IN')}`,
                                colorClass: 'danger',
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveData;
